const { translateString } = window.BAO.Utils

class TagalysQuickSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()
    if (!window?.onTagalysReady) return

    this.initialise = this.initialise.bind(this)
    this.updateSearchSuggestions = this.updateSearchSuggestions.bind(this)
    this.buildProductResults = this.buildProductResults.bind(this)
    this.onClearClick = this.onClearClick.bind(this)

    this.els?.input?.element.addEventListener(
      'input',
      this.updateSearchSuggestions,
    )

    this.els?.clear?.element?.addEventListener('click', this.onClearClick)
    this.els?.close?.element?.addEventListener('click', this.onClearClick)

    this.els?.input?.element?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.onClearClick(e)
    })

    // eslint-disable-next-line no-undef
    onTagalysReady(this.initialise)
  }

  initialise () {
    // eslint-disable-next-line no-undef
    window.searchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
      params: {
        query: this.els?.input?.element.value,
        sections: {
          products: {
            include: ['total_count', 'items'],
            count: 6,
          },
          collections: {
            count: 5,
          },
        },
      },
      onSuccess: (response) => {
        this.buildProductResults(this.els?.products?.element, response)
        this.buildCollectionResults(this.els?.collections?.element, response)
      },
      onFailure (response, helpers) {
        console.error('Failed response:', response)
        console.error('Failed helpers:', helpers)
      },
    })
  }

  setState (state) {
    if (this.getAttribute('data-state') !== state) {
      this.setAttribute('data-state', state)
    }
  }

  updateSearchSuggestions (event) {
    const query = event.target.value || ''
    this._currentQuery = query.trim()
    window.searchSuggestions?.helpers.updateQuery(query)

    this.els?.clear?.element.setAttribute(
      'aria-hidden',
      this._currentQuery.length < 3,
    )

    if (this._currentQuery.length < 3) {
      this.setState('default')
    }
  }

  onClearClick () {
    if (!this.els?.input?.element) return

    this.els.input.element.value = ''
    this._currentQuery = ''
    this._lastRecordedQuery = ''

    window.searchSuggestions?.helpers.updateQuery('')

    this.els.clear.element.setAttribute('aria-hidden', true)
    this.els?.products?.element && (this.els.products.element.innerHTML = '')
    this.els?.collections?.element &&
      (this.els.collections.element.innerHTML = '')

    this.els?.['products-container']?.element?.setAttribute(
      'aria-hidden',
      true,
    )
    this.els?.['suggestion-collections']?.element?.setAttribute(
      'aria-hidden',
      true,
    )
    this.els?.['suggestion-popular']?.element?.setAttribute(
      'aria-hidden',
      false,
    )

    this.setState('default')

    this.els.input.element.focus()
  }

  getEncodedQueryStringFromTagalys (query) {
    const helpers = window.searchSuggestions?.helpers
    const searchQuery = query

    return helpers.getEncodedQueryString({ query: searchQuery })
  }

  buildResultsHref (encodedQueryString, redirectToUrl) {
    if (redirectToUrl) {
      try {
        return `${redirectToUrl}`
      } catch (error) {
        console.error('Failed to parse redirect_to_url:', redirectToUrl, error)
      }
    }

    if (!encodedQueryString) return null

    return `${this.searchPath}?${encodedQueryString}`
  }

  buildSearchLink (queryString) {
    if (typeof queryString !== 'string' || !queryString) return null

    return `${this.searchPath}?${queryString}`
  }

  buildListMarkupFromQueries (items = []) {
    const escapeHtml = (s) =>
      String(s)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;')

    return items
      .map(({ title, queryString, link }) => {
        const href = link || this.buildSearchLink(queryString)
        if (!href) return ''

        return `
        <li class="drw-QuickSearch_SuggestionItem">
          <a href="${href}" class="drw-QuickSearch_SuggestionLink fz-13_143">${escapeHtml(title || '')}</a>
        </li>
      `
      })
      .join('')
  }

  buildCollectionResults (container, response) {
    const items = Array.isArray(response.sections?.collections?.items)
      ? response.sections?.collections.items
      : []
    const html = this.buildListMarkupFromQueries(items)

    if (items.length > 1) {
      container.innerHTML = html
      this.els?.['suggestion-collections']?.element?.setAttribute(
        'aria-hidden',
        !items.length,
      )

      this.els?.['suggestion-popular']?.element?.setAttribute(
        'aria-hidden',
        true,
      )
    }

    return html
  }

  async buildProductResults (container, response) {
    const productsContainer = this.els?.['products-container']?.element
    if (!productsContainer) return ''

    const current = this._currentQuery || ''

    if (current.length < 3) {
      container.innerHTML = ''
      productsContainer.setAttribute('aria-hidden', true)
      productsContainer.removeAttribute('aria-busy')
      this.setState('default')
      return ''
    }

    productsContainer.setAttribute('aria-hidden', false)
    productsContainer.setAttribute('aria-busy', 'true')

    const queryAtStart = current

    const productResultsHtml = await this.createProductCards(
      response.sections.products.items,
    )

    if (
      this._currentQuery !== queryAtStart ||
      (this._currentQuery || '').length < 3
    ) {
      productsContainer.setAttribute('aria-hidden', true)
      productsContainer.removeAttribute('aria-busy')
      this.setState('default')
      return ''
    }

    container.innerHTML = productResultsHtml

    const resultsTotal = response?.sections?.products?.total_count || 0
    const redirectToUrl = response?.metadata?.redirect_to_url
    const encodedQuery = this.getEncodedQueryStringFromTagalys(current)
    const buttonHref = this.buildResultsHref(encodedQuery, redirectToUrl)

    if (resultsTotal > 0) {
      this.els['products-title'].element.innerHTML = translateString(
        this.translations?.quickSearch?.productsTotal,
        { count: `${resultsTotal}` },
      )
      this.els['products-link'].element.setAttribute('href', buttonHref)

      this.setState('results')
    } else {
      this.els['no-results'].element.innerHTML = translateString(
        this.translations?.quickSearch?.noResults,
        { terms: `${current}` },
      )

      this.setState('no-results')
      if (!productResultsHtml.trim()) {
        productsContainer.setAttribute('aria-hidden', true)
      }
    }

    productsContainer.removeAttribute('aria-busy')

    return productResultsHtml
  }

  async createProductCards (products) {
    const cards = await Promise.all(
      products.map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`

        try {
          const res = await window.BAO.sectionRenderer.fetchSingle(
            'tagalys-product-card',
            productUrl,
          )
          const newDOM = window.BAO.sectionRenderer.convertToDOM(res)
          const article = newDOM.querySelector('article')

          return article
            ? `
              <div class="drw-QuickSearch_QueryItem">
                ${article.outerHTML}
              </div>`
            : ''
        } catch (error) {
          this.handleError(error)
          return ''
        }
      }),
    )

    return cards.join('')
  }

  get searchPath () {
    return this.getAttribute('search-path') || '/pages/search'
  }

  get translations () {
    return window.theme.strings
  }
}

if (!customElements.get('tagalys-quick-search')) {
  customElements.define('tagalys-quick-search', TagalysQuickSearch)
}
